/** Setup page — configure heats, riders, divisions, invite judges. Admin only. */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/api/client";
import { useCompetitionStore } from "@/stores/competition";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import type { Division, Heat, HeatRider, Invitation, Rider } from "@/types";

export function SetupPage() {
  const { competitionId } = useParams<{ competitionId: string }>();
  const { competition, heats, riders, divisions, loadCompetition, loadHeats, loadRiders, loadDivisions } =
    useCompetitionStore();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!competitionId) return;
    Promise.all([
      loadCompetition(competitionId),
      loadHeats(competitionId),
      loadRiders(),
      loadDivisions(competitionId),
      api.get<Invitation[]>(`/api/competitions/${competitionId}/invitations`).then(setInvitations),
    ]).then(() => setLoading(false));
  }, [competitionId, loadCompetition, loadHeats, loadRiders, loadDivisions]);

  if (loading || !competition) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Setup: {competition.name}</h1>
          <p className="page-subtitle">Configure divisions, heats, assign riders, invite judges</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        {/* Left: Divisions + Heats */}
        <div className="flex-col gap-6">
          <DivisionManager
            competitionId={competition.id}
            divisions={divisions}
            onRefresh={() => loadDivisions(competition.id)}
          />
          <HeatManager
            competitionId={competition.id}
            heats={heats}
            riders={riders}
            divisions={divisions}
            onRefresh={() => loadHeats(competition.id)}
          />
        </div>

        {/* Right: Settings + Invite */}
        <div className="flex-col gap-6">
          <CompetitionSettings
            competitionId={competition.id}
            scoringModel={competition.scoring_model}
            judgeCount={competition.judge_count}
            overlayDefaults={competition.overlay_defaults}
            onRefresh={() => loadCompetition(competition.id)}
          />
          <InviteManager
            competitionId={competition.id}
            invitations={invitations}
            onRefresh={async () => {
              const inv = await api.get<Invitation[]>(
                `/api/competitions/${competition.id}/invitations`,
              );
              setInvitations(inv);
            }}
          />
        </div>
      </div>
    </div>
  );
}

const SCORING_MODELS = [
  { value: "max", label: "Best Score (Max)", desc: "Rider keeps only their highest judge score" },
];

const OVERLAY_STYLE_OPTIONS = [
  { value: "steel", label: "Steel" },
  { value: "olympic", label: "Olympic" },
  { value: "broadcast", label: "Broadcast" },
  { value: "neon", label: "Neon" },
  { value: "clean", label: "Clean" },
];

interface OverlayDefaults {
  show_all_scores: boolean;
  show_heat_scores: boolean;
  show_rider_scores: boolean;
  show_current_heat: boolean;
  overlay_style: string;
}

const DEFAULT_OVERLAY: OverlayDefaults = {
  show_all_scores: false,
  show_heat_scores: true,
  show_rider_scores: true,
  show_current_heat: true,
  overlay_style: "broadcast",
};

function parseOverlayDefaults(json: string | null): OverlayDefaults {
  if (!json) return { ...DEFAULT_OVERLAY };
  try {
    return { ...DEFAULT_OVERLAY, ...JSON.parse(json) };
  } catch {
    return { ...DEFAULT_OVERLAY };
  }
}

function CompetitionSettings({
  competitionId,
  scoringModel,
  judgeCount,
  overlayDefaults,
  onRefresh,
}: {
  competitionId: string;
  scoringModel: string;
  judgeCount: number;
  overlayDefaults: string | null;
  onRefresh: () => void;
}) {
  const [model, setModel] = useState(scoringModel);
  const [overlay, setOverlay] = useState<OverlayDefaults>(() => parseOverlayDefaults(overlayDefaults));
  const [saving, setSaving] = useState(false);

  const currentOverlay = parseOverlayDefaults(overlayDefaults);
  const dirty = model !== scoringModel || JSON.stringify(overlay) !== JSON.stringify(currentOverlay);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/competitions/${competitionId}`, {
        scoring_model: model,
        overlay_defaults: JSON.stringify(overlay),
      });
      onRefresh();
    } finally {
      setSaving(false);
    }
  };

  const toggleOverlay = (key: keyof OverlayDefaults) => {
    setOverlay((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Competition Settings</div>
      </div>

      <div className="field" style={{ marginBottom: 12 }}>
        <label className="field-label">Scoring Model</label>
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 10px",
            background: "var(--c-surface-2)",
            border: "1px solid var(--c-border)",
            color: "var(--c-text)",
            fontSize: 13,
          }}
        >
          {SCORING_MODELS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <div className="text-sm text-muted" style={{ marginTop: 6 }}>
          {SCORING_MODELS.find((m) => m.value === model)?.desc}
        </div>
      </div>

      <div className="text-sm text-muted" style={{ marginBottom: 16 }}>
        Judges: {judgeCount} (set at creation, cannot be changed)
      </div>

      {/* Overlay defaults — applied when producer clicks Go Live */}
      <div style={{ marginBottom: 16 }}>
        <label className="field-label" style={{ marginBottom: 8 }}>Overlay Defaults (applied on Go Live)</label>
        <div className="flex-col gap-2">
          {([
            { key: "show_current_heat" as const, label: "Show heat name banner" },
            { key: "show_heat_scores" as const, label: "Show heat riders" },
            { key: "show_rider_scores" as const, label: "Show rider scores" },
            { key: "show_all_scores" as const, label: "Show leaderboard" },
          ]).map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2" style={{ fontSize: 13, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={overlay[key] as boolean}
                onChange={() => toggleOverlay(key)}
                style={{ accentColor: "var(--c-accent)" }}
              />
              {label}
            </label>
          ))}
        </div>
        <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
          <label className="field-label" style={{ fontSize: 11 }}>Default overlay style</label>
          <select
            value={overlay.overlay_style}
            onChange={(e) => setOverlay((prev) => ({ ...prev, overlay_style: e.target.value }))}
            style={{
              width: "100%",
              padding: "6px 8px",
              background: "var(--c-surface-2)",
              border: "1px solid var(--c-border)",
              color: "var(--c-text)",
              fontSize: 13,
            }}
          >
            {OVERLAY_STYLE_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {dirty && (
        <Button onClick={save} loading={saving} size="sm">
          Save Changes
        </Button>
      )}
    </div>
  );
}

function DivisionManager({
  competitionId,
  divisions,
  onRefresh,
}: {
  competitionId: string;
  divisions: Division[];
  onRefresh: () => void;
}) {
  const [newDivisionName, setNewDivisionName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const createDivision = async () => {
    if (!newDivisionName.trim()) return;
    await api.post(`/api/competitions/${competitionId}/divisions`, { name: newDivisionName.trim() });
    setNewDivisionName("");
    onRefresh();
  };

  const updateDivision = async (divisionId: string) => {
    if (!editName.trim()) return;
    await api.put(`/api/competitions/${competitionId}/divisions/${divisionId}`, {
      name: editName.trim(),
    });
    setEditingId(null);
    setEditName("");
    onRefresh();
  };

  const deleteDivision = async (divisionId: string) => {
    await api.delete(`/api/competitions/${competitionId}/divisions/${divisionId}`);
    onRefresh();
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Divisions</div>
      </div>

      <div className="flex gap-2" style={{ marginBottom: 16 }}>
        <Input
          placeholder="Division name (e.g. Men's Open)"
          value={newDivisionName}
          onChange={(e) => setNewDivisionName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && createDivision()}
        />
        <Button onClick={createDivision} disabled={!newDivisionName.trim()}>
          Add
        </Button>
      </div>

      {divisions.length === 0 ? (
        <div className="text-sm text-muted" style={{ padding: "8px 0" }}>
          No divisions yet. Divisions are optional — heats can exist without a division.
        </div>
      ) : (
        <div className="flex-col gap-2">
          {divisions.map((division) => (
            <div
              key={division.id}
              className="flex items-center justify-between"
              style={{ padding: "6px 0", borderBottom: "1px solid var(--c-border)" }}
            >
              {editingId === division.id ? (
                <div className="flex gap-2" style={{ flex: 1 }}>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && updateDivision(division.id)}
                    autoFocus
                    style={{ flex: 1 }}
                  />
                  <Button size="sm" onClick={() => updateDivision(division.id)}>
                    Save
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <>
                  <span style={{ fontWeight: 600 }}>{division.name}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingId(division.id);
                        setEditName(division.name);
                      }}
                    >
                      Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => deleteDivision(division.id)}>
                      Delete
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HeatManager({
  competitionId,
  heats,
  riders,
  divisions,
  onRefresh,
}: {
  competitionId: string;
  heats: Heat[];
  riders: Rider[];
  divisions: Division[];
  onRefresh: () => void;
}) {
  const [newHeatName, setNewHeatName] = useState("");
  const [newHeatDivisionId, setNewHeatDivisionId] = useState<string>("");
  const [newHeatMaxAttempts, setNewHeatMaxAttempts] = useState(1);
  const [expandedHeat, setExpandedHeat] = useState<string | null>(null);
  const [heatRiders, setHeatRiders] = useState<Record<string, HeatRider[]>>({});

  const createHeat = async () => {
    if (!newHeatName.trim() || !newHeatDivisionId) return;
    await api.post(`/api/competitions/${competitionId}/heats`, {
      name: newHeatName.trim(),
      division_id: newHeatDivisionId,
      max_attempts: newHeatMaxAttempts,
    });
    setNewHeatName("");
    setNewHeatDivisionId("");
    setNewHeatMaxAttempts(1);
    onRefresh();
  };

  const loadHeatRiders = async (heatId: string) => {
    const hr = await api.get<HeatRider[]>(
      `/api/competitions/${competitionId}/heats/${heatId}/riders`,
    );
    setHeatRiders((prev) => ({ ...prev, [heatId]: hr }));
  };

  const toggleHeat = (heatId: string) => {
    if (expandedHeat === heatId) {
      setExpandedHeat(null);
    } else {
      setExpandedHeat(heatId);
      loadHeatRiders(heatId);
    }
  };

  const addRider = async (heatId: string, riderId: string) => {
    await api.post(`/api/competitions/${competitionId}/heats/${heatId}/riders/${riderId}`);
    loadHeatRiders(heatId);
  };

  const removeRider = async (heatId: string, riderId: string) => {
    await api.delete(`/api/competitions/${competitionId}/heats/${heatId}/riders/${riderId}`);
    loadHeatRiders(heatId);
  };

  const deleteHeat = async (heatId: string) => {
    await api.delete(`/api/competitions/${competitionId}/heats/${heatId}`);
    onRefresh();
  };

  // Group heats by division for display
  const divisionMap = new Map<string | null, Heat[]>();
  for (const heat of heats) {
    const key = heat.division_id;
    if (!divisionMap.has(key)) divisionMap.set(key, []);
    divisionMap.get(key)!.push(heat);
  }

  const getDivisionName = (divisionId: string | null): string | null => {
    if (!divisionId) return null;
    return divisions.find((d) => d.id === divisionId)?.name ?? null;
  };

  // Get the division name for the current expanded heat (used for rider sorting)
  const getExpandedHeatDivisionName = (): string | null => {
    if (!expandedHeat) return null;
    const heat = heats.find((h) => h.id === expandedHeat);
    if (!heat) return null;
    return getDivisionName(heat.division_id);
  };

  // Sort available riders: fuzzy match rider.level against division name first
  const sortRidersByDivisionMatch = (availableRiders: Rider[]): Rider[] => {
    const divisionName = getExpandedHeatDivisionName();
    if (!divisionName) return availableRiders;
    const divLower = divisionName.toLowerCase();
    return [...availableRiders].sort((a, b) => {
      const aLevel = a.level?.toLowerCase() ?? "";
      const bLevel = b.level?.toLowerCase() ?? "";
      const aMatch = aLevel && (divLower.includes(aLevel) || aLevel.includes(divLower));
      const bMatch = bLevel && (divLower.includes(bLevel) || bLevel.includes(divLower));
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      return 0;
    });
  };

  const API_BASE = import.meta.env.VITE_API_URL ?? "";

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Heats</div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div className="flex gap-2" style={{ marginBottom: 8 }}>
          <Input
            placeholder="Heat name..."
            value={newHeatName}
            onChange={(e) => setNewHeatName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createHeat()}
          />
          <Button onClick={createHeat} disabled={!newHeatName.trim() || !newHeatDivisionId}>
            Add
          </Button>
        </div>
        <div className="flex gap-3" style={{ marginTop: 8 }}>
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label className="field-label" style={{ fontSize: 11 }}>
              Division
            </label>
            {divisions.length === 0 ? (
              <div className="text-sm text-muted" style={{ padding: "6px 0" }}>
                Create a division first (above)
              </div>
            ) : (
              <select
                value={newHeatDivisionId}
                onChange={(e) => setNewHeatDivisionId(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  background: "var(--c-surface-2)",
                  border: "1px solid var(--c-border)",
                  color: "var(--c-text)",
                  fontSize: 13,
                }}
              >
                <option value="">Select a division...</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label className="field-label" style={{ fontSize: 11 }}>
              Max Attempts
            </label>
            <div style={{ display: "flex", gap: 4 }}>
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  className={`btn btn-sm ${newHeatMaxAttempts === n ? "btn-primary" : "btn-secondary"}`}
                  style={{ flex: "1 1 0", minWidth: 0, padding: "6px 0" }}
                  onClick={() => setNewHeatMaxAttempts(n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {heats.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No heats</div>
          <div className="empty-state-desc">Create your first heat above.</div>
        </div>
      ) : (
        <div className="flex-col gap-2">
          {Array.from(divisionMap.entries()).map(([divisionId, divisionHeats]) => {
            const divisionName = getDivisionName(divisionId);
            return (
              <div key={divisionId ?? "__none"}>
                {divisionName && (
                  <div
                    className="text-sm text-muted"
                    style={{
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 4,
                      marginTop: 8,
                      fontWeight: 600,
                    }}
                  >
                    {divisionName}
                  </div>
                )}
                {divisionHeats.map((heat) => (
                  <div key={heat.id} className="card" style={{ padding: 0, marginBottom: 4 }}>
                    <button
                      onClick={() => toggleHeat(heat.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        padding: "12px",
                        background: "transparent",
                        border: "none",
                        color: "var(--c-text)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 20,
                            height: 20,
                            fontSize: 12,
                            transition: "transform 0.15s",
                            transform: expandedHeat === heat.id ? "rotate(90deg)" : "rotate(0deg)",
                          }}
                        >
                          &#9654;
                        </span>
                        <span style={{ fontWeight: 700 }}>{heat.name}</span>
                      </div>
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <span className="badge badge-off">Max: {heat.max_score}</span>
                        <span className="badge badge-off">
                          {heat.max_attempts} attempt{heat.max_attempts !== 1 ? "s" : ""}
                        </span>
                        <Button variant="danger" size="sm" onClick={() => deleteHeat(heat.id)}>
                          Delete
                        </Button>
                      </div>
                    </button>

                    {expandedHeat === heat.id && (
                      <div
                        style={{
                          padding: "0 12px 12px",
                          borderTop: "1px solid var(--c-border)",
                        }}
                      >
                        <div className="text-sm text-muted" style={{ marginBottom: 8, marginTop: 12 }}>
                          Riders in heat:
                        </div>
                        {(heatRiders[heat.id] ?? []).map((hr) => (
                          <div
                            key={hr.id}
                            className="flex items-center justify-between"
                            style={{ padding: "4px 0" }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                  flexShrink: 0,
                                  border: "1px solid var(--c-border)",
                                }}
                              >
                                {hr.rider.has_photo ? (
                                  <img
                                    src={`${API_BASE}/api/riders/${hr.rider.id}/photo`}
                                    alt={hr.rider.name}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "var(--c-surface-3)",
                                      color: "#fff",
                                      fontWeight: 700,
                                      fontSize: 12,
                                    }}
                                  >
                                    {hr.rider.name.charAt(0)}
                                  </div>
                                )}
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{hr.rider.name}</div>
                                {hr.rider.nickname && (
                                  <div className="text-muted" style={{ fontSize: 11 }}>
                                    &ldquo;{hr.rider.nickname}&rdquo;
                                  </div>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeRider(heat.id, hr.rider.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                        <div
                          className="text-sm text-muted"
                          style={{ marginTop: 12, marginBottom: 8 }}
                        >
                          Add rider:
                        </div>
                        {sortRidersByDivisionMatch(
                          riders.filter(
                            (r) =>
                              !(heatRiders[heat.id] ?? []).some((hr) => hr.rider.id === r.id),
                          ),
                        ).map((r) => {
                          const divName = getExpandedHeatDivisionName();
                          const rLevel = r.level?.toLowerCase() ?? "";
                          const isMatch =
                            divName &&
                            rLevel &&
                            (divName.toLowerCase().includes(rLevel) ||
                              rLevel.includes(divName.toLowerCase()));
                          return (
                            <div
                              key={r.id}
                              className="flex items-center justify-between"
                              style={{
                                padding: "4px 0",
                                background: isMatch
                                  ? "rgba(59, 130, 246, 0.08)"
                                  : "transparent",
                                borderRadius: 4,
                                paddingLeft: isMatch ? 6 : 0,
                                paddingRight: isMatch ? 6 : 0,
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    overflow: "hidden",
                                    flexShrink: 0,
                                    border: "1px solid var(--c-border)",
                                  }}
                                >
                                  {r.has_photo ? (
                                    <img
                                      src={`${API_BASE}/api/riders/${r.id}/photo`}
                                      alt={r.name}
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "var(--c-surface-3)",
                                        color: "#fff",
                                        fontWeight: 700,
                                        fontSize: 12,
                                      }}
                                    >
                                      {r.name.charAt(0)}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <span className={isMatch ? "" : "text-muted"}>{r.name}</span>
                                  {r.nickname && (
                                    <div className="text-muted" style={{ fontSize: 11 }}>
                                      &ldquo;{r.nickname}&rdquo;
                                    </div>
                                  )}
                                  {r.level && (
                                    <div
                                      className="text-muted"
                                      style={{
                                        fontSize: 10,
                                        color: isMatch ? "var(--c-accent)" : undefined,
                                      }}
                                    >
                                      {r.level}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => addRider(heat.id, r.id)}
                              >
                                Add
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ROLE_COLORS: Record<string, string> = {
  admin: "#3b82f6",
  judge: "#22c55e",
  producer: "#a855f7",
};

function InviteManager({
  competitionId,
  invitations,
  onRefresh,
}: {
  competitionId: string;
  invitations: Invitation[];
  onRefresh: () => void;
}) {
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState<string[]>(["judge"]);
  const [sending, setSending] = useState(false);

  const toggleRole = (role: string) => {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const sendInvite = async () => {
    if (!email.trim() || roles.length === 0) return;
    setSending(true);
    try {
      await api.post(`/api/competitions/${competitionId}/invitations`, {
        email: email.trim(),
        roles,
      });
      setEmail("");
      setRoles(["judge"]);
      onRefresh();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Invite Team</div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div className="flex gap-2" style={{ marginBottom: 8 }}>
          <Input
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ flex: 1 }}
          />
          <Button
            onClick={sendInvite}
            loading={sending}
            disabled={!email.trim() || roles.length === 0}
          >
            Invite
          </Button>
        </div>
        <div className="flex gap-3" style={{ marginTop: 8 }}>
          {(["judge", "producer", "admin"] as const).map((role) => (
            <label
              key={role}
              className="flex items-center gap-2"
              style={{ cursor: "pointer", fontSize: 13 }}
            >
              <input
                type="checkbox"
                checked={roles.includes(role)}
                onChange={() => toggleRole(role)}
                style={{ accentColor: ROLE_COLORS[role] }}
              />
              <span style={{ textTransform: "capitalize" }}>{role}</span>
            </label>
          ))}
        </div>
      </div>
      {invitations.length > 0 && (
        <div className="flex-col gap-1">
          {invitations.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between text-sm"
              style={{ padding: "4px 0" }}
            >
              <span className="text-mono">{inv.email}</span>
              <div className="flex gap-2">
                {inv.roles.map((r) => (
                  <span
                    key={r}
                    className="badge"
                    style={{
                      background: ROLE_COLORS[r] ?? "var(--c-surface-2)",
                      color: "#fff",
                      textTransform: "capitalize",
                      fontSize: 11,
                    }}
                  >
                    {r}
                  </span>
                ))}
              </div>
              <span className="text-muted">{inv.accepted_at ? "Accepted" : "Pending"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
